import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  User,
  updateProfile,
  sendPasswordResetEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification
} from 'firebase/auth';
import { auth } from './config';



const actionCodeSettings = {
  // Use the auth action handler URL for all Firebase auth actions
  url: 'https://bananacup.be/auth/action',
  handleCodeInApp: true
};

export const signUp = async (email: string, password: string, displayName: string) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update the user profile with display name
    if (auth.currentUser) {
      await updateProfile(auth.currentUser, {
        displayName
      });
      
      // Send email verification with custom redirect URL
      await sendEmailVerification(auth.currentUser, actionCodeSettings);
    }
    
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

export const logIn = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

export const logOut = async () => {
  try {
    await signOut(auth);
    return true;
  } catch (error) {
    throw error;
  }
};

export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

export const onAuthChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, (user) => {
    callback(user);
  });
};

export const resetPassword = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Send email verification to the currently logged in user
 */
export const sendVerificationEmail = async () => {
  try {
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('No user is currently logged in');
    }
    
    await sendEmailVerification(user, actionCodeSettings);
    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Check if the current user's email is verified and update the verification status if needed
 * This is useful when a user verifies their email from a separate tab/window
 */
export const checkEmailVerificationStatus = async (): Promise<boolean> => {
  try {
    const user = auth.currentUser;
    
    if (!user) {
      return false;
    }
    
    // Reload user to get fresh verification status
    await user.reload();
    return user.emailVerified;
  } catch (error) {
    console.error('Error checking email verification status:', error);
    return false;
  }
};

/**
 * Reauthenticate a user with their email and password
 * Used for sensitive operations like account deletion
 */
export const reauthenticateUser = async (password: string) => {
  try {
    const user = auth.currentUser;
    
    if (!user || !user.email) {
      throw new Error('No user is currently logged in or user has no email');
    }
    
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
    return true;
  } catch (error) {
    throw error;
  }
};
